import { useEffect, useRef, useState } from "react";
import { firstVisibleOffset } from "@/lib/scheduleOverlay";

/** Finds the element that actually scrolls (a Radix ScrollArea hides it inside). */
export type ScrollerResolver = () => HTMLElement | null;

/**
 * Live `scrollLeft` of a horizontally scrolling container, as a render value.
 *
 * Keep this in a LEAF component. Both timelines are large trees, and a value
 * that changes on every scroll frame would re-render all of it — the hour
 * labels and the time pill are the only things that care.
 */
export function useHorizontalScrollOffset(resolve: ScrollerResolver): number {
  const [scrollLeft, setScrollLeft] = useState(0);
  // Held in a ref so callers don't have to memoize the resolver to keep the
  // subscription from tearing down on every render.
  const resolveRef = useRef(resolve);
  resolveRef.current = resolve;

  useEffect(() => {
    const element = resolveRef.current();
    if (!element) return;

    // Browsers already coalesce scroll events to one per frame, and React
    // bails out when the value is unchanged (vertical scrolling of the same
    // container), so a plain handler is enough.
    const sync = () => setScrollLeft(element.scrollLeft);

    sync();
    element.addEventListener("scroll", sync, { passive: true });
    return () => element.removeEventListener("scroll", sync);
  }, []);

  return scrollLeft;
}

/**
 * Smallest timeline offset that is not hidden behind the sticky label column,
 * live while the timeline scrolls. Anything centred on the timeline (hour
 * labels, the current-time pill) stays hidden below this so it can never be
 * drawn over the stage and custom-subject names.
 */
export function useLabelColumnClearance(resolve: ScrollerResolver, canvasPadding = 0): number {
  return firstVisibleOffset(useHorizontalScrollOffset(resolve), canvasPadding);
}
